import React, { PureComponent, useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import { withTranslation, useTranslation } from 'react-i18next';

import { makeGetMilestone, makeGetSortedActiveProjectMilestones } from '../selectors/milestones';
import { makeGetProject } from '../selectors/projects';
import { makeGetTaskGroup } from 'selectors/task-groups';

import { fetchMilestone, updateMilestone, removeMilestone, setMilestoneVisibility } from '../actions/milestones';
import { fetchTasks, archiveTasks } from '../actions/tasks';
import { updateTaskGroup } from '../actions/task-groups';
import { setCustomFieldValue } from '../actions/custom-fields';
import { createRecursionRule, updateRecursionRule, removeRecursionRule } from '../actions/recursion-rules';
import { duplicateMilestone } from '../api/milestones-api';

import { createAlert } from '../services/alert-service';
import { getMilestoneType, getMilestoneProgress } from '../utils/milestone';
import { isTempId } from '../utils/helpers';
import { getUserPreference, setUserPreference } from '../services/user-preferences-service';

import RecursionRuleContainer from '../containers/recursion-rule-container';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';
import { LocaleContext } from 'context/LocaleContext';

import CanUser from './can-user';
import DropdownElement from './dropdown-element';
import DescriptionPane from './description-pane';
import DateRangeElement from './date-range-element';
import TaskSingle from './task-single';
import CustomFields from './custom-fields';
import FeatureAvailable from './feature-available';
import CustomFieldsUpgradeNotice from './custom-fields-upgrade-notice';
import ResourceRepeatPopout from './resource-repeat-popout';
import Tooltip from './tooltip';
import ResourceRepeatText from './resource-repeat-text';
import HideFrom from './hide-from';
import SelectMilestonePopout from './select-milestone-popout';
import MultiLineInput from 'components/MultiLineInput';
import MilestoneTasks from 'components/MilestoneTasks';
import DateTime from 'components/DateTime';
import HelpTooltip from 'components/help-tooltip';
import MilestoneActions from 'components/MilestoneActions';

import IconClose from '../icons/icon-close';
import IconLock from '../icons/icon-lock';
import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowLeft from '../icons/icon-arrow-left';
import IconCustomField from '../icons/icon-custom-field';
import IconRepeat from '../icons/icon-repeat';
import IconCalendar from '../icons/icon-calendar';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconEdit from '../icons/icon-edit';
import IconArchive from '../icons/icon-archive';
import IconNumber from '../icons/icon-number';
import IconEye from '../icons/icon-eye';
import IconExpand from '../icons/icon-expand';
import IconCollapse from '../icons/icon-collapse';

const ALLOWED_ACTIONS = ['duplicate', 'move', 'createTemplate', 'remove', 'removeAndArchiveTasks'];

const DurationControl = ({ start, end }) => {
    const localeCtx = useContext(LocaleContext);
    const { t } = useTranslation('date');

    const duration = useMemo(() => {
        return localeCtx?.differenceInDays(new Date(end), new Date(start)) + 1;
    }, [start, end, localeCtx]);

    return (
        <div className="control">
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconCalendarSimple />
                    </span>

                    <span className="control-text">
                        { t('date:Duration') }
                    </span>
                </div>

                <div className="control-value">
                    { t('date:daysCount', { count: duration }) }
                </div>
            </div>
        </div>
    );
}

class MilestoneSingle extends PureComponent {
    static contextType = RolesAndPermissionsContext;

    static defaultProps = {
        large: true
    };

    state = {
        name: this.name,
        currentTaskId: null,
        collapsed: getUserPreference('content_panel_collapsed') || false
    }

    constructor(props) {
        super(props);

        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    handleKeyDown(event) {
        if (event.which === 27 && (event.target.nodeName !== 'INPUT' && event.target.nodeName !== 'TEXTAREA')) {
            this.close();
        }
    }

    handleNameBlur = nextName => {
        const nextProps = { name: nextName };

        if (this.props.milestone && this.props.milestone.task_group) {
            updateTaskGroup(this.props.milestone.task_group, nextProps)
        } else {
            this.update(this.props.id, nextProps);
        }
    }

    handleDescriptionChange = description => {
        this.update(this.props.id, { description });
    }

    handleDependencySelect = dependency => {
        const { t } = this.props;
        const dep = this.props.milestones.find(m => m.id === dependency);

        if (dep && new Date(dep.start) > new Date(this.props.milestone.start)) {
            createAlert('default', t('alerts.cantSetDepThatIsAfter'));
            return;
        }

        this.update(this.props.id, { dependency });
    }

    handleDependencyRemove = () => {
        this.update(this.props.id, { dependency: null });
    }

    handleMilestoneReveal = roleIds => {
        setMilestoneVisibility(this.props.id, true, roleIds);
    }

    handleMilestoneHide = roleIds => {
        setMilestoneVisibility(this.props.id, false, roleIds);
    }

    handleRemove = () => {
        this.close();
    }

    handleSizeToggle = () => {
        setUserPreference('content_panel_collapsed', !this.state.collapsed);

        this.setState({
            collapsed: !this.state.collapsed
        });
    }

    handleStartEndChange = ({ start, end }) => {
        this.update(this.props.id, {
            start,
            end
        });
    }

    handleTaskClick = taskId => {
        if (isTempId(taskId)) {
            return;
        }

        this.setState({
            currentTaskId: taskId
        });
    }

    handleTaskClose = () => {
        this.closeTask();
    }

    handleCustomFieldChange = (id, value) => {
        setCustomFieldValue('milestone', this.props.id, id, value);
    }

    handleCustomFieldCreateClick = () => {
        this.customFieldsCtrl && this.customFieldsCtrl.create();
    }

    handleRepeatMilestoneUpdate = ({ type, trigger, cron, till, bi_weekly }) => {
        if (this.props.milestone.rule) {
            updateRecursionRule(this.props.milestone.rule, {
                type,
                trigger,
                cron,
                till,
                bi_weekly
            })

            return;
        }

        createRecursionRule({
            milestone: this.props.id,
            type,
            trigger,
            cron,
            till,
            bi_weekly
        });
    }

    handleRepeatDisable = () => {
        removeRecursionRule(this.props.milestone.rule);
    }

    handleArchiveClick = () => {
        this.update(this.props.id, {
            archived: true
        });

        this.close();
    }

    handleUnarchiveClick = () => {
        this.update(this.props.id, {
            archived: false
        });

        this.close();
    }

    closeTask() {
        this.setState({
            currentTaskId: null
        });
    }

    update(milestoneId, milestoneProps, sourceMilestoneId) {
        if (milestoneId === sourceMilestoneId) {
            // Break circular dependency
            return;
        }

        if (!sourceMilestoneId) {
            sourceMilestoneId = milestoneId;
        }

        if (milestoneProps.end) {
            const milestone = this.props.milestones.find(milestone => milestone.id === milestoneId);

            if (milestone) {
                this.updateDependentMilestones(milestoneId, milestoneProps.end - milestone.end, sourceMilestoneId);
            }
        }

        updateMilestone(milestoneId, milestoneProps);
    }

    updateDependentMilestones(milestoneId, diff, sourceMilestoneId) {
        if (!diff) {
            return;
        }

        this.props.milestones
            .forEach(milestone => {
                if (milestone.dependency === milestoneId) {
                    this.update(milestone.id, {
                        start: new Date(milestone.start).getTime() + diff,
                        end: new Date(milestone.end).getTime() + diff
                    }, sourceMilestoneId);
                }
            });
    }

    close() {
        if (this.props.onBack) {
            this.props.onBack();
            return;
        }

        this.props.onClose && this.props.onClose();
    }

    attemptFetchMilestone(force) {
        if (
            !force && (
                !this.props.id ||
                (this.props.milestone && !(this.props.milestone.shouldRefetch && !this.props.milestone.loading))
            )
        ) {
            return;
        }

        fetchMilestone(this.props.id);
    }

    setNewTaskCtrl = ctrl => this.newTaskCtrl = ctrl;
    setCustomFieldsCtrl = ctrl => this.customFieldsCtrl = ctrl;
    setDateRangeCtrl = ctrl => this.dateRangeCtrl = ctrl;

    getMilestoneName(milestone) {
        if (milestone) {
            if (milestone.task_group) {
                const taskGroup = this.props.taskGroup;
                if (taskGroup && taskGroup.name) {
                    return taskGroup.name;
                }
            } else {
                if (milestone.name) {
                    return milestone.name;
                }
            }
        }

        return '';
    }

    get name() {
        return this.getMilestoneName(this.props.milestone);
    }

    get readableType() {
        const { t } = this.props;

        switch (this.type) {
            case 'overdue':
                return t('types.Overdue');

            case 'completed':
                return t('types.Completed');

            case 'default':
                return t('types.Not Started');

            default:
                return t('types.In Progress');
        }
    }

    get type() {
        return getMilestoneType(this.props.milestone);
    }

    get progress() {
        return getMilestoneProgress(this.props.milestone);
    }

    get large() {
        return this.props.large && !this.state.collapsed;
    }

    get element() {
        const { id, milestone, t } = this.props;

        if (!milestone) {
            return null;
        }

        if (!('name' in milestone) || milestone.error) {
            return null;
        }

        const tasksElement = <MilestoneTasks id={id} onTaskClick={this.handleTaskClick} />

        const hidden = (this.props.taskGroup || this.props.milestone)?.hidden;

        return (
            <div className="content-panel">
                <div className="content-panel-main">
                    <div className="content-panel-head">
                        <div className="head-main">
                            <div className="content-panel-head-top">
                                <div className="content-panel-head-utils">
                                    {
                                        this.props.onBack ? (
                                            <button className="content-panel-control btn content-panel-back" onClick={this.props.onBack}><IconArrowLeft size="20px" /></button>
                                        ) : null
                                    }

                                    <span className={'left-connected tag-pill ' + this.type}>{this.progress}%</span>
                                </div>

                                <div className="content-panel-controls">
                                    <div className="content-panel-simple-actions">
                                        <CanUser type="editMilestone">
                                            <div className={'content-panel-simple-action' + (hidden?.length ? ' active' : '')}>
                                                <HideFrom
                                                    hidden={hidden}
                                                    creator={this.props.milestone?.created_by}
                                                    onHide={this.handleMilestoneHide}
                                                    onReveal={this.handleMilestoneReveal}
                                                >
                                                    <div>
                                                        <Tooltip tooltip={hidden?.length ? (
                                                            <span>{ t('Milestone hidden from', {
                                                                roles: this.context?.getRolesListString(hidden, true)
                                                            }) }</span>
                                                        ) : (
                                                            <span>{t('actions.Hide Milestone from')}</span>
                                                        )}>
                                                            <div className="content-panel-simple-action-inner">
                                                                <IconEye size="16px" />
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                </HideFrom>
                                            </div>
                                        </CanUser>

                                        <CanUser type="editMilestone" customList={[milestone.created_by]}>
                                            {
                                                milestone.archived ? (
                                                    <Tooltip tooltip={ t('actions.Unarchive milestone') }>
                                                        <div className="content-panel-simple-action" onClick={this.handleUnarchiveClick}>
                                                            <div className="content-panel-simple-action-inner">
                                                                <IconArchive />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip tooltip={ t('actions.Archive milestone') }>
                                                        <div className="content-panel-simple-action" onClick={this.handleArchiveClick}>
                                                            <div className="content-panel-simple-action-inner">
                                                                <IconArchive />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                )
                                            }
                                        </CanUser>
                                    </div>

                                    <MilestoneActions
                                        id={this.props.id}
                                        className="content-panel-control"
                                        innerClassName="content-panel-actions-trigger"
                                        allowedActions={ALLOWED_ACTIONS}
                                        onRemove={this.handleRemove}
                                    />

                                    {
                                        this.props.large && !this.props.inside ? (
                                            <button className="content-panel-control btn content-panel-size-toggle" onClick={this.handleSizeToggle}>
                                                {
                                                    this.state.collapsed ? (
                                                        <IconExpand />
                                                    ) : (
                                                        <IconCollapse />
                                                    )
                                                }
                                            </button>
                                        ) : null
                                    }

                                    {
                                        this.props.onClose ? (
                                            <button className="content-panel-control btn content-panel-close" onClick={this.props.onClose}><IconClose /></button>
                                        ) : null
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="content-panel-main-inner">
                        <div className="content-panel-side">
                            <div className="content-panel-title">
                                <MultiLineInput
                                    className="content-panel-field-input"
                                    placeholder={ t('Add milestone name') }
                                    canEdit={this.context?.canUser('editMilestone', [milestone.created_by])}
                                    value={this.name}
                                    onBlur={this.handleNameBlur}
                                />
                            </div>

                            <div className="content-panel-scrollable nice-scroll">
                                <div className="content-panel-block">
                                    <DescriptionPane
                                        key={this.props.id}
                                        editable={this.context?.canUser('editMilestone')}
                                        placeholder={ t('Add milestone description') }
                                        content={this.props.milestone.description || ''}
                                        onChange={this.handleDescriptionChange}
                                    />
                                </div>

                                <div className="content-panel-block">
                                    <div className="content-panel-utils">
                                        {
                                            this.props.milestone.start && this.props.milestone.end ? (
                                                <React.Fragment>
                                                    <div className="control">
                                                        <div className="control-inner">
                                                            <div className="control-label">
                                                                <span className="control-icon">
                                                                    <IconCalendar />
                                                                </span>

                                                                <span className="control-text">
                                                                    { t('Start & End Date') }
                                                                </span>
                                                            </div>

                                                            {
                                                                this.context?.canUser('editMilestone', [milestone.created_by]) && !this.props.autoMilestones ? (
                                                                    <DateRangeElement
                                                                        title={ t('Select the start and end dates') }
                                                                        value={this.props.milestone}
                                                                        onChange={this.handleStartEndChange}
                                                                        provideController={this.setDateRangeCtrl}
                                                                    >
                                                                        <div className="control-value">
                                                                            <DateTime format="fullDateShort">{this.props.milestone.start}</DateTime> - <DateTime format="fullDateShort">{this.props.milestone.end}</DateTime>

                                                                            <span className="control-value-edit">
                                                                                <IconEdit />
                                                                            </span>
                                                                        </div>
                                                                    </DateRangeElement>
                                                                ) : (
                                                                    <div className="control-value">
                                                                        <DateTime format="fullDateShort">{this.props.milestone.start}</DateTime> - <DateTime format="fullDateShort">{this.props.milestone.end}</DateTime>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </div>

                                                    <DurationControl start={this.props.milestone.start} end={this.props.milestone.end} />
                                                </React.Fragment>
                                            ) : null
                                        }

                                        <div className="control">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconLock />
                                                    </span>

                                                    <span className="control-text">
                                                        { t('common:Dependency') }
                                                    </span>
                                                </div>

                                                <CanUser type="editMilestone">
                                                    <SelectMilestonePopout
                                                        title={ t('common:Select dependency') }
                                                        projectId={milestone.project}
                                                        selected={milestone.dependency}
                                                        onSelect={this.handleDependencySelect}
                                                        onRemove={this.handleDependencyRemove}
                                                        removeCopy={ t('common:Remove dependency') }
                                                        excluded={[this.props.id, ...this.props.milestones.filter(milestone => milestone.dependency === this.props.id).map(m => m.id)]}
                                                        xAlign="left"
                                                        defaultXPosition="right"
                                                    >
                                                        <span className="control-value">
                                                            {
                                                                this.props.dependency ? this.getMilestoneName(this.props.dependency) : <span className="action-text">{ t('common:Set') }…</span>
                                                            }
                                                        </span>
                                                    </SelectMilestonePopout>
                                                </CanUser>

                                                <CanUser type="editMilestone" reverse={true}>
                                                    <span className="control-value">
                                                        {
                                                            this.props.dependency ? this.getMilestoneName(this.props.dependency) : t('No Milestone Dependency')
                                                        }
                                                    </span>
                                                </CanUser>
                                            </div>
                                        </div>

                                        {
                                            !this.props.public ? (
                                                <div className="control">
                                                    <div className="control-inner">
                                                        <div className="control-label">
                                                            <span className="control-icon">
                                                                <IconRepeat />
                                                            </span>

                                                            <span className="control-text">
                                                                { t('recurring:Recurring') }
                                                            </span>
                                                        </div>

                                                        <div className="control-value">
                                                            {
                                                                this.props.milestone.rule ? (
                                                                    <RecursionRuleContainer id={this.props.milestone.rule}>
                                                                        <ResourceRepeatText />
                                                                    </RecursionRuleContainer>
                                                                ) : (
                                                                    <span className="action-text">{ t('common:Set') }…</span>
                                                                )
                                                            }
                                                        </div>

                                                        <CanUser type="editMilestone" customList={[milestone.created_by]}>
                                                            <RecursionRuleContainer id={this.props.milestone.rule}>
                                                                <ResourceRepeatPopout
                                                                    key={this.props.id}
                                                                    title={ t('actions.Repeat this Milestone') }
                                                                    projectId={this.props.milestone.project}
                                                                    xAlign="right"
                                                                    defaultXPosition="left"
                                                                    rule={this.props.milestone.rule}
                                                                    types={['date']}
                                                                    onChange={this.handleRepeatMilestoneUpdate}
                                                                    onDisable={this.handleRepeatDisable}
                                                                >
                                                                    <div className="popout-toggle" />
                                                                </ResourceRepeatPopout>
                                                            </RecursionRuleContainer>
                                                        </CanUser>
                                                    </div>
                                                </div>
                                            ) : null
                                        }

                                        {
                                            milestone.statistic ? (
                                                <div className="control">
                                                    <div className="control-inner">
                                                        <div className="control-label">
                                                            <span className="control-icon">
                                                                <IconNumber />
                                                            </span>

                                                            <span className="control-text">
                                                                { t('common:Story Points') }

                                                                <HelpTooltip>
                                                                    { t('common:storyPoints.help') } <a href="https://help.niftypm.com/en/articles/3827830-using-story-points-to-balance-resources-agile-scrum-workflow" target="_blank" rel="noopener noreferrer">{ t('common:Learn More') }</a>
                                                                </HelpTooltip>
                                                            </span>
                                                        </div>

                                                        <div className="control-value">
                                                            { milestone.statistic.completed_story_points }/{ milestone.statistic.total_story_points }
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                </div>

                                <div className="content-panel-body">
                                    <div className="content-panel-body-main">
                                        <div className="hide-on-large">
                                            { tasksElement }
                                        </div>

                                        {
                                            !this.props.public ? (
                                                <div className="content-panel-section">
                                                    <div className="section-head">
                                                        <h4>
                                                            <span className="title-icon"><IconCustomField /></span>

                                                            <span className="text">{ t('fields:Custom Fields') }</span>

                                                            <FeatureAvailable type="customFields">
                                                                <CanUser type="editCustomFields">
                                                                    <button className="btn btn-circle btn-xxsmall btn-simple btn-icon btn-colored" onClick={this.handleCustomFieldCreateClick}>+</button>
                                                                </CanUser>
                                                            </FeatureAvailable>
                                                        </h4>
                                                    </div>
                                                    <div className="section-body">
                                                        <FeatureAvailable type="customFields">
                                                            <CustomFields
                                                                key={this.props.id}
                                                                projectId={this.props.milestone.project}
                                                                type="row-single"
                                                                association="milestone"
                                                                associationId={this.props.id}
                                                                values={this.props.milestone.fields}
                                                                onChange={this.handleCustomFieldChange}
                                                                provideController={this.setCustomFieldsCtrl}
                                                            />
                                                        </FeatureAvailable>

                                                        <FeatureAvailable type="customFields" reverse={true}>
                                                            <CustomFieldsUpgradeNotice />
                                                        </FeatureAvailable>
                                                    </div>
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {
                            this.large ? (
                                <div className="content-panel-side">
                                    <div className="content-panel-scrollable nice-scroll">
                                        <div className="content-panel-body">
                                            <div className="content-panel-body-main">
                                                { tasksElement }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }
                    </div>
                </div>
            </div>
        )
    }

    componentDidMount() {
        if (this.props.milestone && (this.props.milestone.error || this.props.milestone.removed)) {
            this.close();
            return;
        }

        if (this.props.id) {
            this.attemptFetchMilestone(true);
        }

        window.addEventListener('keydown', this.handleKeyDown);

        if (this.props.datepickerOnInit) {
            this.dateRangeCtrl && this.dateRangeCtrl.show();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    componentDidUpdate(prevProps) {
        if (this.props.milestone && (this.props.milestone.error || this.props.milestone.removed)) {
            this.close();
            return;
        }

        if (this.props.id !== prevProps.id) {
            this.closeTask();

            if (this.props.id) {
                this.attemptFetchMilestone(true);
            } else {
                this.attemptFetchMilestone();
            }
        }
    }

    render() {
        return (
            <div className={'content-panel-holder' + (this.props.id ? ' visible' : '') + (this.props.inside ? ' inside' : '') + (this.state.currentTaskId ? ' subview-visible' : '') + (this.large ? ' large' : '')}>
                { this.element }

                {
                    this.props.id ? (
                        <TaskSingle
                            id={this.state.currentTaskId}
                            inside={true}
                            large={this.large}
                            noRedirect={true}
                            shouldChangeUrl={false}
                            public={this.props.public}
                            onBack={this.handleTaskClose}
                            onClose={this.props.onClose}
                        />
                    ) : null
                }
            </div>
        )
    }
}

const makeMapStateToProps = () => {
    const getMilestone = makeGetMilestone();
    const getTaskGroup = makeGetTaskGroup();
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getProject = makeGetProject();

    return ({ milestones, tasks, taskGroups, projects }, ownProps) => {
        const milestone = getMilestone(milestones.items, ownProps.id);
        const projectId = milestone?.project;

        return {
            milestone,
            autoMilestones: Boolean(projectId && getProject(projects.items, projectId)?.auto_milestones),
            taskGroup: milestone && milestone.task_group && getTaskGroup(taskGroups.items, milestone.task_group),
            dependency: milestone && milestone.dependency && getMilestone(milestones.items, milestone.dependency),
            milestones: (projectId && getSortedActiveProjectMilestones(milestones.items, milestone.project)) || []
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation(['milestone', 'common', 'recurring', 'fields', 'date'])(MilestoneSingle));
